import { useCallback, useEffect, useMemo, useState } from 'react';

import { useSelector } from 'react-redux';

import { RouteProp, useRoute } from '@react-navigation/native';

import { SCREEN } from 'router/screens.ts';
import { RouteNavigationParams } from 'router/types.ts';

import { selectTemplateviewRailById } from 'store/railsBuilder/selectors.ts';

import { ContentType } from 'services/data-manager/types.ts';

import { getSettledResponseData } from 'utils/axios/nagra/utils.ts';

import { DetailsPageSeasonsTab } from '../../../types.ts';
import { noSeasonName } from '../constants.ts';
import { SeasonListItem } from '../types.ts';
import useProgramEpisodes from './useProgramEpisodes.ts';
import useProgramSeasons from './useProgramSeasons.ts';
import useRecordingEpisodes from './useRecordingEpisodes.ts';
import useRecordingSeasons from './useRecordingSeasons.ts';
import useVodEpisodes from './useVodEpisodes.ts';
import useVodSeasons from './useVodSeasons.ts';

type Params = {
    isVod: boolean;
    seriesId: string;
    channelId: string | undefined;
    initialSeason: typeof noSeasonName | number;
};

function useSeasonsAndEpisodes({
    isVod,
    seriesId,
    channelId,
    initialSeason,
}: Params) {
    const route = useRoute<RouteProp<RouteNavigationParams, SCREEN.DETAILS>>();

    const rail = useSelector(
        selectTemplateviewRailById(
            route.params?.templateId,
            route.params?.railId,
        ),
    );

    const [loading, setLoading] = useState(false);
    const [seasonEpisodesLoading, setSeasonEpisodesLoading] = useState(false);
    const [selectedTab, setSelectedTab] = useState(
        rail?.properties?.ContentType === ContentType.NPVR
            ? DetailsPageSeasonsTab.RECORDINGS
            : DetailsPageSeasonsTab.PROGRAMS,
    );
    const [selectedSeasonNumber, setSelectedSeasonNumber] =
        useState<SeasonListItem['seasonNumber']>(initialSeason);

    const { seasons: vodSeasons, onLoadSeasons: onLoadVodSeasons } =
        useVodSeasons();
    const { seasons: programSeasons, onLoadSeasons: onLoadProgramSeasons } =
        useProgramSeasons();
    const { seasons: recordingSeasons, onLoadSeasons: onLoadRecordingSeasons } =
        useRecordingSeasons();

    const {
        episodes: vodEpisodes,
        onLoadEpisodes: onLoadVodEpisodes,
        onLoadNextEpisodes: onLoadNextVodEpisodes,
    } = useVodEpisodes({
        seriesId,
        channelId,
        initialSeason,
        selectedSeasonNumber,
    });
    const {
        episodes: programEpisodes,
        onLoadEpisodes: onLoadProgramEpisodes,
        onLoadNextEpisodes: onLoadNextProgramEpisodes,
    } = useProgramEpisodes({
        seriesId,
        channelId,
        initialSeason,
        selectedSeasonNumber,
    });
    const {
        episodes: recordingEpisodes,
        onLoadEpisodes: onLoadRecordingEpisodes,
        onLoadNextEpisodes: onLoadNextRecordingEpisodes,
    } = useRecordingEpisodes({
        seriesId,
        initialSeason,
        selectedSeasonNumber,
        recordingSeasons,
    });

    const handleSetSelectedSeasonNumber = useCallback(
        async (_seasonNumber: typeof initialSeason) => {
            setSeasonEpisodesLoading(true);
            setSelectedSeasonNumber(_seasonNumber);

            if (isVod) {
                await onLoadVodEpisodes(seriesId, channelId, _seasonNumber, 1);
            } else {
                await Promise.allSettled([
                    onLoadProgramEpisodes(
                        seriesId,
                        channelId,
                        _seasonNumber,
                        1,
                    ),
                    onLoadRecordingEpisodes(_seasonNumber, recordingSeasons, 1),
                ]);
            }

            setSeasonEpisodesLoading(false);
        },
        [
            channelId,
            isVod,
            onLoadProgramEpisodes,
            onLoadRecordingEpisodes,
            onLoadVodEpisodes,
            recordingSeasons,
            seriesId,
        ],
    );

    const handleLoadNextEpisodes = useCallback(async () => {
        if (selectedSeasonNumber != null) {
            if (isVod) {
                await onLoadNextVodEpisodes();
            } else if (selectedTab === DetailsPageSeasonsTab.RECORDINGS) {
                await onLoadNextRecordingEpisodes();
            } else if (selectedTab === DetailsPageSeasonsTab.PROGRAMS) {
                await onLoadNextProgramEpisodes();
            }
        }
    }, [
        isVod,
        onLoadNextProgramEpisodes,
        onLoadNextRecordingEpisodes,
        onLoadNextVodEpisodes,
        selectedSeasonNumber,
        selectedTab,
    ]);

    const selectedSeason = useMemo(() => {
        if (isVod) {
            return vodSeasons.find(
                ({ seasonNumber }) => selectedSeasonNumber === seasonNumber,
            );
        }

        if (selectedTab === DetailsPageSeasonsTab.PROGRAMS) {
            return programSeasons.find(
                ({ seasonNumber }) => selectedSeasonNumber === seasonNumber,
            );
        }

        if (selectedTab === DetailsPageSeasonsTab.RECORDINGS) {
            return recordingSeasons.find(
                ({ seasonNumber }) => selectedSeasonNumber === seasonNumber,
            );
        }

        return undefined;
    }, [
        isVod,
        programSeasons,
        recordingSeasons,
        selectedSeasonNumber,
        selectedTab,
        vodSeasons,
    ]);

    const seasons = useMemo(() => {
        if (isVod) {
            return vodSeasons;
        }

        if (selectedTab === DetailsPageSeasonsTab.PROGRAMS) {
            return programSeasons;
        }

        if (selectedTab === DetailsPageSeasonsTab.RECORDINGS) {
            return recordingSeasons;
        }

        return [];
    }, [isVod, programSeasons, recordingSeasons, selectedTab, vodSeasons]);

    const episodes = useMemo(() => {
        if (selectedSeasonNumber != null) {
            if (isVod) {
                return vodEpisodes;
            }

            if (selectedTab === DetailsPageSeasonsTab.PROGRAMS) {
                return programEpisodes;
            }

            if (selectedTab === DetailsPageSeasonsTab.RECORDINGS) {
                return recordingEpisodes;
            }
        }

        return [];
    }, [
        selectedSeasonNumber,
        isVod,
        selectedTab,
        vodEpisodes,
        programEpisodes,
        recordingEpisodes,
    ]);

    useEffect(() => {
        (async () => {
            try {
                setLoading(true);

                if (isVod) {
                    await Promise.allSettled([
                        onLoadVodSeasons(seriesId, channelId),
                        onLoadVodEpisodes(
                            seriesId,
                            channelId,
                            selectedSeasonNumber,
                            1,
                        ),
                    ]);
                } else {
                    const [, recordingsSeasonsSettledResponse] =
                        await Promise.allSettled([
                            onLoadProgramSeasons(seriesId, channelId),
                            onLoadRecordingSeasons(seriesId),
                        ]);

                    const recordingsSeasonsList = getSettledResponseData(
                        recordingsSeasonsSettledResponse,
                    );
                    await Promise.allSettled([
                        onLoadProgramEpisodes(
                            seriesId,
                            channelId,
                            selectedSeasonNumber,
                            1,
                        ),
                        onLoadRecordingEpisodes(
                            selectedSeasonNumber,
                            recordingsSeasonsList,
                            1,
                        ),
                    ]);

                    if (rail?.properties?.ContentType === ContentType.NPVR) {
                        setSelectedTab(DetailsPageSeasonsTab.RECORDINGS);
                    } else {
                        setSelectedTab(DetailsPageSeasonsTab.PROGRAMS);
                    }
                }

                setLoading(false);
            } catch (error) {
                if (__DEV__) {
                    console.log('Seasons error: ', error);
                }

                setLoading(false);
            }
        })();
    }, []);

    useEffect(() => {
        if (selectedSeason == null) {
            const firstAvailableSeason = seasons[0];

            if (firstAvailableSeason) {
                handleSetSelectedSeasonNumber(
                    firstAvailableSeason.seasonNumber,
                );
            }
        }
    }, [selectedTab]);

    return {
        loading,
        seasonEpisodesLoading,

        showTabs: !!(programSeasons.length && recordingSeasons.length),
        selectedTab,
        selectedSeasonNumber,
        selectedSeason,

        seasons,
        episodes,

        onSetSelectedTab: setSelectedTab,
        onSetSelectedSeasonNumber: handleSetSelectedSeasonNumber,
        onLoadNextEpisodes: handleLoadNextEpisodes,
    };
}

export default useSeasonsAndEpisodes;
